import queryString from 'query-string';

const PARSE_OPTIONS = { arrayFormat: 'bracket' };
const STRINGIFY_OPTIONS = {
  arrayFormat: 'bracket',
  skipNull: true,
  skipEmptyString: true,
};

/**
 * @param {string} [search] - `location.search` or `?a=1`
 * @returns {Record<string, string | string[] | null | undefined>}
 */
export function parseSearchParams(search) {
  if (!search) return {};
  const normalized = search.startsWith('?') ? search : `?${search}`;
  return queryString.parse(normalized, PARSE_OPTIONS);
}

/**
 * @param {Record<string, unknown>} params
 * @returns {string} - includes leading `?` when non-empty
 */
export function stringifySearchParams(params) {
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    cleaned[key] = value;
  }
  const str = queryString.stringify(cleaned, STRINGIFY_OPTIONS);
  return str ? `?${str}` : '';
}

/**
 * Merge updates into the current query string. Omitting a key in `updates` leaves it unchanged.
 * Set a key to `null`, `undefined`, or `''` to remove it.
 *
 * @param {string} currentSearch - `location.search`
 * @param {Record<string, string | string[] | null | undefined>} updates
 * @returns {string} - full search string (with leading `?` or empty)
 */
export function mergeSearchParams(currentSearch, updates) {
  const current = parseSearchParams(currentSearch);
  const next = { ...current };
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === null || value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return stringifySearchParams(next);
}

/**
 * Stable comparison for avoiding redundant `history.replace` calls.
 * @param {string} a - `location.search`
 * @param {string} b - candidate search string
 */
export function areSearchStringsEqual(a, b) {
  return normalizeSearchString(a) === normalizeSearchString(b);
}

/**
 * Parse + stable stringify so router `location.search` and our merged strings compare equal.
 */
export function normalizeSearchString(search) {
  const params = parseSearchParams(search);
  const sortedKeys = Object.keys(params).sort();
  const sorted = {};
  for (const k of sortedKeys) {
    sorted[k] = params[k];
  }
  return stringifySearchParams(sorted);
}
